import { createContext, useContext } from "react";
import { PropsWithChildren } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { UserProfile } from "../general/user";
import Config from "../general/config";
import { useLocation } from 'react-router-dom';

export interface IUser {
    sub: string;
    nickname?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    picture?: string;
    roles?: string[];
    profile: UserProfile;
}

export interface IAuth {
  login: (returnTo?: string) => Promise<void>;
  signup: (returnTo?: string) => Promise<void>;
  logout: () => void;

  getUser: () => IUser | undefined;

  getIsAuthenticated: () => boolean;
  getIsLoading: () => boolean;

  getAccessToken: () => Promise<string>;
}

export const AuthContext = createContext<IAuth | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const currentDestination = location.pathname === '/403' && typeof location.state?.returnTo === 'string'
    ? location.state.returnTo : location.pathname + location.search + location.hash;
  const {
    loginWithRedirect,
    logout,
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently
  } = useAuth0();

  const auth: IAuth = {
    login: (returnTo) => loginWithRedirect({ appState: { returnTo: returnTo ?? currentDestination }, authorizationParams: { scope: "openid profile email" }}),
    signup: (returnTo) => loginWithRedirect({ appState: { returnTo: returnTo ?? currentDestination }, authorizationParams: { screen_hint: "signup" }}),
    logout: () => logout({ logoutParams: { returnTo: window.location.origin } }),

    getIsAuthenticated: () => isAuthenticated,
    getIsLoading: () => isLoading,

    getUser: () =>
      user
        ? {
            sub: user.sub!,
            nickname: user.nickname,
            name: user.name,
            given_name: user.given_name,
            family_name: user.family_name,
            email: user.email,
            picture: user.picture,
            roles: user[Config.auth0.roles_claims] ?? [],
            profile: user[Config.auth0.user_medatata_claims] ?? {}
          }
        : undefined,

    getAccessToken: () => getAccessTokenSilently()
  };

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
